import { useCallback, useEffect, useState } from "react";
import { apiAuthFetch } from "@/lib/authFetch";
import i18n from "@/lib/i18n";

export const SETTINGS_INSTRUCTIONS_MAX_LENGTH = 5_000;

type SettingsDto = {
  instructions?: string;
};

type UseSettingsResult = {
  instructions: string;
  loading: boolean;
  error: string | null;
  save: (newInstructions: string) => Promise<string>;
};

function normalizeInstructions(
  payload: SettingsDto | null | undefined,
): string {
  return typeof payload?.instructions === "string" ? payload.instructions : "";
}

async function readErrorMessage(response: Response): Promise<string> {
  try {
    const body = (await response.clone().json()) as {
      detail?: string;
      title?: string;
      message?: string;
      error?: string;
    };
    const message = body.detail ?? body.title ?? body.message ?? body.error;
    if (typeof message === "string" && message.trim().length > 0) {
      return message;
    }
  } catch {
    // Ignore parse failures and fall back to a localized status message.
  }

  if (response.status === 400) return i18n.t("error.code.bad_request");
  if (response.status === 401) return i18n.t("error.code.unauthorized");
  if (response.status === 403) return i18n.t("error.code.forbidden");
  if (response.status === 404) return i18n.t("error.code.not_found");
  if (response.status >= 500) return i18n.t("error.code.service_unavailable");
  return i18n.t("error.code.unknown");
}

function useSettings(endpoint: string): UseSettingsResult {
  const [instructions, setInstructions] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadSettings = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await apiAuthFetch(endpoint);
      if (!response.ok) {
        throw new Error(await readErrorMessage(response));
      }

      const payload = (await response.json()) as SettingsDto;
      setInstructions(normalizeInstructions(payload));
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : i18n.t("error.code.service_unavailable"),
      );
    } finally {
      setLoading(false);
    }
  }, [endpoint]);

  useEffect(() => {
    void loadSettings();
  }, [loadSettings]);

  const save = useCallback(
    async (newInstructions: string) => {
      if (newInstructions.length > SETTINGS_INSTRUCTIONS_MAX_LENGTH) {
        const message = i18n.t("error.code.bad_request");
        setError(message);
        throw new Error(message);
      }

      const response = await apiAuthFetch(endpoint, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ instructions: newInstructions }),
      });

      if (!response.ok) {
        const message = await readErrorMessage(response);
        setError(message);
        throw new Error(message);
      }

      const payload = (await response.json()) as SettingsDto;
      const updatedInstructions = normalizeInstructions(payload);
      setInstructions(updatedInstructions);
      setError(null);
      return updatedInstructions;
    },
    [endpoint],
  );

  return {
    instructions,
    loading,
    error,
    save,
  };
}

export function useUserSettings(): UseSettingsResult {
  return useSettings("/api/settings/user");
}

export function useTenantSettings(): UseSettingsResult {
  return useSettings("/api/settings/tenant");
}
